import { type CancellationReason, type EvidenceKind, type ICancellationRow, TERMINAL_EVIDENCE_CODES } from './cancellation-api';
import type { IRelaySession } from './media-relay';

// On-device record of the agent's cancellation report, one per (election, polling unit).
// Written BEFORE anything goes over the network, so a report made with no signal is never lost:
// the flusher (cancellation-sync.ts) picks it up on `online` and on app start. Records are
// never silently deleted — after the retry ladder runs out a step is `held` and waits for a
// manual Retry, as with the support-chat outbox.
//
// v2 (MediaStash relay): evidence uploads through api.core's relay session, resumed from the
// server's `receivedChunks`. A v1 record is migrated once on read (`migrateCancellationRecord`);
// only its evidence changes. The storage key keeps its `v1` prefix so nothing is orphaned.

export type DeliveryState =
	| 'queued'
	| 'sending'
	| 'sent'
	/** Retries exhausted — waiting for the agent to tap Retry. */
	| 'held'
	/** The server refused it for good (not assigned, unknown code). Never retried. */
	| 'refused'
	/** The agent withdrew it before it ever left the phone. */
	| 'cancelled';

export interface IDelivery {
	state: DeliveryState;
	attempts: number;
	nextAttemptAt: number;
	error?: string;
	code?: string;
}

export type EvidenceState =
	| 'queued'
	| 'uploading'
	| 'uploaded'
	| 'registered'
	| 'held'
	/** Server refused the file for good (too large, media refused, another report's asset). */
	| 'rejected'
	/** The bytes are gone (no IndexedDB, page reloaded). Needs re-attaching. */
	| 'missing'
	/** The server didn't accept evidence when last asked. Saved on the phone; re-checked. */
	| 'unsupported';

export interface IEvidenceItem {
	id: string;
	kind: EvidenceKind;
	mime: string;
	bytes: number;
	name: string;
	durationSec?: number;
	capturedAt: string;
	state: EvidenceState;
	attempts: number;
	nextAttemptAt: number;
	/** Set once storage holds the whole file. */
	storagePublicId?: string;
	/** api.core upload session — reused while live so a dropped connection resumes. */
	session?: IRelaySession;
	sha256?: string;
	error?: string;
	/** Last failure code — `STORAGE_UNAVAILABLE` / `STORAGE_NOT_READY` read as "paused on the server". */
	code?: string;
	/** An uncoded 400 has already been retried once; the next one is final. */
	badRequestRetried?: boolean;
}

export type IncidentState = 'pending' | 'created' | 'linked' | 'skipped' | 'held';

export interface ICancellationRecord {
	v: 2;
	/** Minted at the Send tap and reused on every retry — the server's idempotency key. */
	referenceId: string;
	electionId: number;
	/** Core user id of the filer — another agent on a shared phone never sends it. */
	userId?: number;
	pollingUnitCode: string;
	pollingUnitName?: string;
	reason: CancellationReason;
	notes?: string;
	/** When the agent tapped Send (taken as when INEC declared it). */
	cancelledAt: string;
	createdAt: number;
	coords?: { latitude: number; longitude: number; accuracy?: number | null } | null;
	report: IDelivery;
	/** When the server accepted the report — "Party alerted at …". */
	sentAt?: string;
	row?: ICancellationRow | null;
	alreadyCancelled?: boolean;
	/** Report went through the v1 bulk fallback; no withdraw, evidence or incident link. */
	baseline?: boolean;
	evidence: IEvidenceItem[];
	incident: IDelivery & { incidentState: IncidentState; vesrIncidentId?: string };
	withdrawnAt?: string;
	/** Set once {@link rescueEvidence} has run on this record. */
	evidenceRescue?: 1;
}

const PREFIX = 'pu-cancel:v1:';
const recordKey = (electionId: number, puCode: string) => `${PREFIX}${electionId}:${puCode}`;
const serverKey = (electionId: number, puCode: string) => `${PREFIX}server:${electionId}:${puCode}`;
const CHANGED_EVENT = 'pu-cancel:changed';

const notifyChanged = (electionId: number, puCode: string) => {
	try {
		window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: { electionId, puCode } }));
	} catch {
		// Non-browser environment — nothing to notify.
	}
};

export const subscribeCancellationChanged = (electionId: number, puCode: string, onChange: () => void): (() => void) => {
	const handler = (event: Event) => {
		const detail = (event as CustomEvent<{ electionId: number; puCode: string }>).detail;
		if (detail?.electionId === electionId && detail?.puCode === puCode) onChange();
	};
	// The `storage` event covers a second tab writing the same record.
	const onStorage = (event: StorageEvent) => {
		if (event.key === recordKey(electionId, puCode) || event.key === serverKey(electionId, puCode)) onChange();
	};
	window.addEventListener(CHANGED_EVENT, handler);
	window.addEventListener('storage', onStorage);
	return () => {
		window.removeEventListener(CHANGED_EVENT, handler);
		window.removeEventListener('storage', onStorage);
	};
};

const isRecord = (v: unknown): v is ICancellationRecord =>
	!!v &&
	typeof v === 'object' &&
	(v as ICancellationRecord).v === 2 &&
	typeof (v as ICancellationRecord).referenceId === 'string';

const UNFINISHED: EvidenceState[] = ['queued', 'uploading', 'uploaded', 'held'];

/**
 * v1 → v2, once. Evidence that hadn't registered was going to the retired Cloudinary cloud: its
 * `storagePublicId`/`url` are dropped (the server now verifies the asset in MediaStash, so the
 * file must go up again) and it returns to `queued` with a fresh retry ladder — `held` included,
 * so files stalled by the outage upload without a tap. The report and incident steps are
 * untouched, and no blob is.
 */
export const migrateCancellationRecord = (legacy: Record<string, unknown>): ICancellationRecord => {
	const evidence = Array.isArray(legacy.evidence) ? (legacy.evidence as (IEvidenceItem & { url?: string })[]) : [];
	return {
		...(legacy as unknown as ICancellationRecord),
		v: 2,
		evidence: evidence.map(({ url: _url, ...item }) =>
			UNFINISHED.includes(item.state)
				? { ...item, state: 'queued', attempts: 0, nextAttemptAt: 0, storagePublicId: undefined, error: undefined }
				: item
		),
	};
};

/** A file back at the start of the line, with a fresh retry ladder and no dead session. */
const requeued = (item: IEvidenceItem): IEvidenceItem => ({
	...item,
	state: 'queued',
	attempts: 0,
	nextAttemptAt: 0,
	storagePublicId: undefined,
	session: undefined,
	error: undefined,
	code: undefined,
	badRequestRetried: undefined,
});

/** Parked or refused for a reason that can change — "Try again" and the one-shot rescue may requeue it. */
export const canRetryEvidence = (item: IEvidenceItem): boolean =>
	item.state === 'unsupported' || (item.state === 'rejected' && !(item.code && TERMINAL_EVIDENCE_CODES.includes(item.code)));

/**
 * Once per record: files parked by older builds — `unsupported` from a session-long cached
 * "no evidence", or `rejected` by any 4xx — go back to `queued`, but only where the bytes are
 * still on the phone (a file without them can only be re-attached). A `MEDIA_REJECTED` /
 * `EVIDENCE_BELONGS_ELSEWHERE` refusal stays put.
 */
export const rescueEvidence = async (
	record: ICancellationRecord,
	hasBlob: (evidenceId: string) => Promise<boolean>
): Promise<ICancellationRecord> => {
	if (record.evidenceRescue === 1) return record;
	const ids = new Set<string>();
	for (const item of record.evidence) {
		if (canRetryEvidence(item) && (await hasBlob(item.id))) ids.add(item.id);
	}
	return (
		updateRecord(record.electionId, record.pollingUnitCode, (r) => ({
			...r,
			evidenceRescue: 1,
			evidence: r.evidence.map((e) => (ids.has(e.id) && canRetryEvidence(e) ? requeued(e) : e)),
		})) ?? record
	);
};

/** The agent's "Try again" on one refused or parked file. */
export const requeueEvidence = (electionId: number, puCode: string, evidenceId: string): ICancellationRecord | null =>
	updateRecord(electionId, puCode, (r) => ({
		...r,
		evidence: r.evidence.map((e) => (e.id === evidenceId && canRetryEvidence(e) ? requeued(e) : e)),
	}));

const parseRecord = (key: string, raw: string | null): ICancellationRecord | null => {
	const parsed = raw ? JSON.parse(raw) : null;
	if (isRecord(parsed)) return parsed;
	if (
		!parsed ||
		typeof parsed !== 'object' ||
		(parsed as { v?: unknown }).v !== 1 ||
		typeof (parsed as { referenceId?: unknown }).referenceId !== 'string'
	) {
		return null;
	}
	const migrated = migrateCancellationRecord(parsed);
	try {
		localStorage.setItem(key, JSON.stringify(migrated));
	} catch {
		// Storage unavailable — migrated again on the next read.
	}
	return migrated;
};

export const readRecord = (electionId: number, puCode: string): ICancellationRecord | null => {
	try {
		const key = recordKey(electionId, puCode);
		return parseRecord(key, localStorage.getItem(key));
	} catch {
		return null;
	}
};

// In-session copy for when storage throws, so the flow still works online.
const memory = new Map<string, ICancellationRecord>();

export const writeRecord = (record: ICancellationRecord): ICancellationRecord => {
	const key = recordKey(record.electionId, record.pollingUnitCode);
	try {
		localStorage.setItem(key, JSON.stringify(record));
		memory.delete(key);
	} catch {
		// Storage unavailable (or full) — the in-memory copy carries this session.
		memory.set(key, record);
	}
	notifyChanged(record.electionId, record.pollingUnitCode);
	return record;
};

/** Read with the in-memory fallback. */
export const getRecord = (electionId: number, puCode: string): ICancellationRecord | null =>
	readRecord(electionId, puCode) ?? memory.get(recordKey(electionId, puCode)) ?? null;

export const updateRecord = (
	electionId: number,
	puCode: string,
	update: (record: ICancellationRecord) => ICancellationRecord
): ICancellationRecord | null => {
	const current = getRecord(electionId, puCode);
	if (!current) return null;
	return writeRecord(update(current));
};

export const updateEvidence = (
	electionId: number,
	puCode: string,
	evidenceId: string,
	patch: Partial<IEvidenceItem>
): ICancellationRecord | null =>
	updateRecord(electionId, puCode, (r) => ({
		...r,
		evidence: r.evidence.map((e) => (e.id === evidenceId ? { ...e, ...patch } : e)),
	}));

/** Every record on this device — the app-start flush walks these. */
export const listRecords = (): ICancellationRecord[] => {
	const out = new Map<string, ICancellationRecord>(memory);
	try {
		for (let i = 0; i < localStorage.length; i += 1) {
			const key = localStorage.key(i);
			if (!key || !key.startsWith(PREFIX) || key.startsWith(`${PREFIX}server:`)) continue;
			const stored = parseRecord(key, localStorage.getItem(key));
			if (stored) out.set(key, stored);
		}
	} catch {
		// Storage unavailable — memory only.
	}
	return [...out.values()];
};

/** Last server row seen for the unit (any reporter, incl. an admin entry) — instant paint. */
export const readServerRow = (electionId: number, puCode: string): ICancellationRow | null => {
	try {
		const raw = localStorage.getItem(serverKey(electionId, puCode));
		return raw ? (JSON.parse(raw) as ICancellationRow) : null;
	} catch {
		return null;
	}
};

export const writeServerRow = (electionId: number, puCode: string, row: ICancellationRow | null) => {
	try {
		if (row) localStorage.setItem(serverKey(electionId, puCode), JSON.stringify(row));
		else localStorage.removeItem(serverKey(electionId, puCode));
	} catch {
		// Storage unavailable.
	}
	notifyChanged(electionId, puCode);
};

const freshDelivery = (): IDelivery => ({ state: 'queued', attempts: 0, nextAttemptAt: 0 });

export const newRecord = (input: {
	referenceId: string;
	electionId: number;
	userId?: number;
	pollingUnitCode: string;
	pollingUnitName?: string;
	reason: CancellationReason;
	notes?: string;
	cancelledAt: string;
	createdAt: number;
	coords?: ICancellationRecord['coords'];
	evidence?: IEvidenceItem[];
}): ICancellationRecord => ({
	v: 2,
	...input,
	evidence: input.evidence ?? [],
	report: freshDelivery(),
	incident: { ...freshDelivery(), incidentState: 'pending' },
});

export const newEvidenceItem = (input: {
	id: string;
	kind: EvidenceKind;
	mime: string;
	bytes: number;
	name: string;
	durationSec?: number;
	capturedAt: string;
}): IEvidenceItem => ({ ...input, state: 'queued', attempts: 0, nextAttemptAt: 0 });

// ---------------------------------------------------------------------------
// Derived views
// ---------------------------------------------------------------------------

/** Is there an open (not withdrawn / refused / reinstated) cancellation for this unit? */
export interface ICancellationSnapshot {
	active: boolean;
	reason?: CancellationReason;
	cancelledAt?: string;
	/** True while the report only exists on this phone. */
	localOnly: boolean;
	row?: ICancellationRow | null;
}

export const snapshotOf = (record: ICancellationRecord | null, serverRow: ICancellationRow | null): ICancellationSnapshot => {
	// A server row is the truth once we have one — an admin may have confirmed, reinstated or
	// entered it, or another device may have reported it.
	// Exception: a report filed on this phone AFTER the cached row (e.g. re-filed once an old one
	// was withdrawn) is newer than anything the cache knows about.
	const recordRow = record?.row ?? null;
	let row = serverRow ?? recordRow;
	if (
		serverRow &&
		record &&
		!record.withdrawnAt &&
		recordRow?.id !== serverRow.id &&
		record.createdAt > (Date.parse(serverRow.recordedAt || '') || 0)
	) {
		row = recordRow;
	}
	if (row) {
		const active = (row.state ?? 'Active') === 'Active' && !(record?.withdrawnAt && record.row?.id === row.id);
		return { active, reason: row.reason, cancelledAt: row.cancelledAt, localOnly: false, row };
	}
	if (record && !record.withdrawnAt && record.report.state !== 'refused' && record.report.state !== 'cancelled') {
		return { active: true, reason: record.reason, cancelledAt: record.cancelledAt, localOnly: record.report.state !== 'sent' };
	}
	return { active: false, localOnly: false };
};

export const readSnapshot = (electionId: number, puCode: string): ICancellationSnapshot =>
	snapshotOf(getRecord(electionId, puCode), readServerRow(electionId, puCode));
